import { loadSwiper, observeOnce, prefersReducedMotion } from '@kame/lib';

class KameProductStory extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;

    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('.kame-pstory__panel'));
    this.tablist = this.querySelector('[role="tablist"]');
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.listeners = new AbortController();
    const options = { signal: this.listeners.signal };

    this.visible = false;
    this.observer = new IntersectionObserver(([entry]) => {
      this.visible = this.isConnected && entry.isIntersecting;
      this.syncVideo();
    });
    this.observer.observe(this);

    this.motion.addEventListener('change', () => this.syncVideo(), options);
    this.addEventListener('click', (event) => {
      const tab = event.target.closest('[role="tab"]');
      if (tab && this.tabs.includes(tab)) this.select(tab);
    }, options);

    this.addEventListener('keydown', (event) => {
      const current = this.tabs.indexOf(document.activeElement);
      if (current === -1) return;

      let next = null;
      if (event.key === 'ArrowRight') next = (current + 1) % this.tabs.length;
      else if (event.key === 'ArrowLeft') next = (current - 1 + this.tabs.length) % this.tabs.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = this.tabs.length - 1;

      if (next !== null) {
        event.preventDefault();
        this.select(this.tabs[next], { focus: true });
      }
    }, options);

    if (this.tabs.length > 1 && this.tabs.every((tab) => this.panelFor(tab))) {
      this.select(this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0]);
      this.classList.add('is-armed');
      this.tablist.hidden = false;
    }
    this.cinema = this.dataset.layout === 'cinema';
    if (this.cinema) {
      this.classList.add('is-armed');
      this.revealObserver = observeOnce(this, () => this.classList.add('is-inview'), '0px 0px -20% 0px');
    }
    this.deck = this.querySelector('[data-pstory-deck]');
    if (this.deck && this.deck.querySelectorAll('.kame-pstory__card').length > 1) {
      this.mobile = matchMedia('(max-width: 749px)');
      this.mobile.addEventListener('change', () => this.syncDeck(), options);
      this.syncDeck();
    }
    this.ready = true;
    this.syncVideo();
  }

  disconnectedCallback() {
    this.destroyDeck();
    this.observer?.disconnect();
    this.observer = null;
    this.revealObserver?.disconnect();
    this.revealObserver = null;
    this.listeners?.abort();
    this.visible = false;
    this.querySelectorAll('[data-pstory-video]').forEach((video) => video.pause());
    this.ready = false;
  }

  async syncDeck() {
    if (!this.mobile.matches) {
      this.destroyDeck();
      return;
    }
    if (this.swiper || this.deckLoading) return;
    this.deckLoading = true;
    let Swiper;
    try {
      Swiper = await loadSwiper(this.dataset.swiperSrc);
    } catch {
      return;
    } finally {
      this.deckLoading = false;
    }
    if (!this.isConnected || this.swiper || !this.mobile.matches) return;

    const list = this.deck.querySelector('.kame-pstory__cards');
    const cards = list.querySelectorAll('.kame-pstory__card');
    this.deck.classList.add('swiper');
    this.deck.setAttribute('role', 'region');
    this.deck.setAttribute('aria-roledescription', 'carousel');
    list.classList.add('swiper-wrapper');
    cards.forEach((card) => card.classList.add('swiper-slide'));

    const reduced = prefersReducedMotion();
    this.swiper = new Swiper(this.deck, {
      effect: 'coverflow',
      centeredSlides: true,
      slidesPerView: 'auto',
      initialSlide: Math.floor(cards.length / 2),
      speed: reduced ? 0 : 520,
      grabCursor: true,
      slideToClickedSlide: true,
      a11y: { slideRole: 'listitem' },
      coverflowEffect: { rotate: 38, stretch: 0, depth: 170, modifier: 1, slideShadows: false },
      pagination: { el: this.querySelector('[data-pstory-dots]'), clickable: true },
    });
  }

  destroyDeck() {
    if (!this.swiper) return;
    this.swiper.destroy(true, true);
    this.swiper = null;
    this.deck.classList.remove('swiper');
    this.deck.removeAttribute('role');
    this.deck.removeAttribute('aria-roledescription');
    this.deck.querySelector('.kame-pstory__cards')?.classList.remove('swiper-wrapper');
    this.deck.querySelectorAll('.kame-pstory__card').forEach((card) => card.classList.remove('swiper-slide'));
  }

  panelFor(tab) {
    return this.panels.find((panel) => panel.id === tab.getAttribute('aria-controls'));
  }

  select(tab, { focus = false } = {}) {
    this.tabs.forEach((candidate) => {
      const isTarget = candidate === tab;
      candidate.setAttribute('aria-selected', String(isTarget));
      candidate.tabIndex = isTarget ? 0 : -1;

      const panel = this.panelFor(candidate);
      if (!panel) return;
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', candidate.id);
      panel.tabIndex = 0;
      if (isTarget) panel.removeAttribute('inert');
      else panel.setAttribute('inert', '');
    });

    if (focus) tab.focus();
    this.syncVideo();
  }

  syncVideo() {
    this.querySelectorAll('[data-pstory-video]').forEach((video) => {
      video.controls = this.motion.matches;
      if (this.visible && this.isConnected && !this.motion.matches) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }
}

if (!customElements.get('kame-product-story')) customElements.define('kame-product-story', KameProductStory);
